import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Zoom,
  IconButton,
  Paper,
  Typography,
  Box,
  Hidden,
} from '@material-ui/core';
import ExpandLessRoundedIcon from '@material-ui/icons/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { makeStyles } from '@material-ui/core/styles';
import { withTheme } from '@material-ui/core/styles';

import Chart from './Chart';
import Bar from './Bar';
import ProductSavings from './ProductSavings';

import usePrevious from '../hooks/usePrevious';

const useStyles = makeStyles((theme) => ({
  cssGrid: {
    height: '100%',
    minHeight: '100%',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      // gridTemplateRows: '1fr 1fr 2fr',
      gridTemplateRows: 'auto auto 1fr',
      gridTemplateAreas: `
        "bars"
        "summaryList"
        "chart"`,
      gridGap: `${theme.spacing(1)}px ${theme.spacing(1)}px`, // row-gap column-gap
      alignItems: 'stretch', // 'center', // center top-bottom (vertical)
      justifyItems: 'stretch', // 'center', // center left-right (horizontal)
    },
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '2fr 1fr',
      gridTemplateAreas: `
        "chart chart" 
        "summaryList bars"`,
      gridGap: `${theme.spacing(1)}px ${theme.spacing(1)}px`, // row-gap column-gap
      alignItems: 'stretch', // 'center', // center top-bottom (vertical)
      justifyItems: 'stretch', // 'center' // center left-right (horizontal)
    },
    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gridTemplateRows: 'auto auto', // '1fr 1fr',
      gridTemplateAreas: `
        "chart chart summaryList" 
        "chart chart bars"`,
      gridGap: `${theme.spacing(2)}px ${theme.spacing(2)}px`, // row-gap column-gap
      alignItems: 'stretch', // 'center', // center top-bottom (vertical)
      justifyItems: 'stretch', // 'center' // center left-right (horizontal)
    },
  },
  paper: {
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      top: 56,
      left: 0,
      right: 0,
      bottom: 0,
      // height: 'calc(100vh - 80px)',
      height: 'calc(100% - 80px)',
      width: 'auto',
      zIndex: theme.zIndex.appBar,
      padding: theme.spacing(2),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      overflow: 'auto',
    },
    [theme.breakpoints.up('sm')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '40vh',
      width: 'auto',
      zIndex: theme.zIndex.appBar,
      padding: theme.spacing(2),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      overflow: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '40vh',
      width: 'auto',
      zIndex: theme.zIndex.appBar,
      padding: theme.spacing(3),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      overflow: 'auto',
    },
    '&::-webkit-scrollbar': {
      width: '5px',
      backgroundColor: 'rgba(0, 0, 0, 0)',
      WebkitBorderRadius: '100px',
    },
    '&::-webkit-scrollbar-hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.09)',
    },
    '&::-webkit-scrollbar-thumb:vertical': {
      background: '#dadada',
      WebkitBorderRadius: '100px',
    },
    '&::-webkit-scrollbar-thumb:vertical:active': {
      background: 'rgba(0, 0, 0, 0.6)',
      WebkitBorderRadius: '100px',
    },
  },
  boxWrapper: {
    height: '100%',
  },
  gridContainer: {
    height: '100%',
  },
  hideButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    margin: theme.spacing(1),
    zIndex: theme.zIndex.appBar + 1,
  },
  showButton: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    margin: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  chartContainer: {
    minHeight: '250px',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    gridArea: 'chart',
  },
  summaryContainer: {
    gridArea: 'summaryList',
    alignSelf: 'center',
    justifySelf: 'center',
  },
  barsContainer: {
    gridArea: 'bars',
    alignSelf: 'center',
  },
}));

let round = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const ProductSummary = ({
  chartData,
  dateRange,
  locationTitle,
  markerMode,
  theme: { palette },
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [avgCost, setAvgCost] = useState({
    single: 85,
    zero: 50,
  });
  const [firstLoad, setFirstLoad] = useState(true);
  const prevMarkerMode = usePrevious(markerMode);

  useEffect(() => {
    if (!chartData.single.length || !chartData.zero.length) return;

    let calcSavings = (dataArray) => {
      let totalCost = dataArray
        .slice(dateRange[0] - 1972, dateRange[1] - 1972)
        .reduce((accumulator, currentValue) => (accumulator += currentValue));
      return round(totalCost / (dateRange[1] - dateRange[0]));
    };

    let single = calcSavings(chartData.single);
    let zero = calcSavings(chartData.zero);

    setAvgCost({ single, zero });
  }, [dateRange, chartData]);

  useEffect(() => {
    if (open && markerMode && markerMode !== prevMarkerMode) {
      setOpen(false);
    }
  }, [markerMode, prevMarkerMode, open, setOpen]);

  let handleSummary = () => {
    setOpen(!open);
  };

  return (
    <div>
      <Slide
        direction='up'
        in={open}
        timeout={{ enter: 750, exit: 500 }}
        style={{ transitionDelay: firstLoad ? '1500ms' : '0ms' }}
        mountOnEnter
        unmountOnExit
        onEntered={() => setFirstLoad(false)}
      >
        <Paper elevation={4} className={classes.paper}>
          <Box
            display='flex'
            flexDirection='column'
            className={classes.boxWrapper}
          >
            <Box display='flex' flexDirection='row'>
              <Box
                flexGrow={1}
                overflow='hidden'
                textOverflow='ellipsis'
                whiteSpace='nowrap'
                px={8}
              >
                <Typography variant='h5' align='center' noWrap>
                  {locationTitle || null}
                </Typography>
              </Box>
              <IconButton
                onClick={handleSummary}
                color='secondary'
                size='small'
                aria-label='Hide'
                className={classes.hideButton}
              >
                <ExpandMoreRoundedIcon />
              </IconButton>
            </Box>
            <Box flexGrow={1}>
              <div className={classes.cssGrid}>
                <Box className={classes.chartContainer} p={{ xs: 1, md: 2 }}>
                  <Chart data={chartData} dateRange={dateRange} />
                </Box>

                <Box
                  className={classes.summaryContainer}
                  py={{ xs: 3, md: 2 }}
                  px={{ xs: 2, md: 4 }}
                >
                  <ProductSavings
                    singleSavings={round(100 - avgCost.single)}
                    zeroSavings={round(100 - avgCost.zero)}
                    dateRange={dateRange}
                  />
                </Box>

                <Box
                  className={classes.barsContainer}
                  py={{ xs: 4, md: 2 }}
                  px={{ xs: 2, md: 4 }}
                >
                  <Bar
                    percentage={100}
                    name='Unlimited Event'
                    color={palette.product.unlimited.light}
                  />
                  <Bar
                    percentage={avgCost.single}
                    name='Single Event'
                    color={palette.product.single.light}
                  />
                  <Bar
                    percentage={avgCost.zero}
                    name='Zero Event'
                    color={palette.product.zero.light}
                  />
                </Box>
              </div>
            </Box>
          </Box>
        </Paper>
      </Slide>
      {!open && chartData.single.length > 0 && (
        <div>
          <Hidden only='xs'>
            <Zoom
              in={!open}
              timeout={{ enter: 250, exit: 200 }}
              style={{ transitionDelay: `${open ? 0 : 350}ms` }}
              unmountOnExit
            >
              <IconButton
                className={classes.showButton}
                onClick={handleSummary}
                color='secondary'
                size='medium'
                aria-label='show product summary'
              >
                <ExpandLessRoundedIcon />
              </IconButton>
            </Zoom>
          </Hidden>
          <Hidden smUp>
            <Zoom
              in={!open}
              timeout={{ enter: 250, exit: 200 }}
              style={{ transitionDelay: `${open ? 0 : 500}ms` }}
              unmountOnExit
            >
              <IconButton
                className={`${classes.showButton} MuiFab-extended`}
                onClick={handleSummary}
                variant='extended'
                color='secondary'
                size='medium'
                aria-label='show product summary'
              >
                <Box fontSize={14} fontWeight={500}>
                  SHOW SUMMARY
                </Box>
                <ExpandLessRoundedIcon />
              </IconButton>
            </Zoom>
          </Hidden>
        </div>
      )}
    </div>
  );
};

ProductSummary.propTypes = {
  chartData: PropTypes.object.isRequired,
  dateRange: PropTypes.array.isRequired,
  locationTitle: PropTypes.string,
  markerMode: PropTypes.bool.isRequired,
  theme: PropTypes.object.isRequired,
};

export default withTheme(ProductSummary);
